<template>
  <div class="flow-settings-form">
    <!-- Campaign Name -->
    <div class="flow-settings-form__field" data-field="name">
      <label class="flow-settings-form__label">
        {{ t('automatedScenarios.globalBulkSettings.name.label') }}
      </label>
      <InputText
        v-model="localSettings.name"
        class="flow-settings-form__input"
        :class="{ 'p-invalid': validationErrors.name?.length }"
        @update:model-value="emitUpdate('name', $event)"
      />
      <div v-if="validationErrors.name?.length" class="field-error-message">
        <i class="far fa-exclamation-circle" />
        <small class="p-error">{{ validationErrors.name[0] }}</small>
      </div>
    </div>

    <!-- Description / Legend -->
    <div class="flow-settings-form__field" data-field="legend">
      <label class="flow-settings-form__label">
        {{ t('automatedScenarios.globalSettings.legend.label') }}
      </label>
      <InputText
        v-model="localSettings.legend"
        class="flow-settings-form__input"
        :placeholder="t('automatedScenarios.globalSettings.legend.placeholder')"
        @update:model-value="emitUpdate('legend', $event)"
      />
    </div>

    <!-- Track Sales -->
    <div class="flow-settings-form__field" data-field="track_sales">
      <ButtonGroup
        :model-value="localSettings.track_sales ?? 1"
        :options="yesNoOptions"
        title="automatedScenarios.globalSettings.track_sales.label"
        tooltip-desc="automatedScenarios.globalSettings.track_sales.description"
        custom-class-title="flow-settings-form__label"
        tooltip-class="flow-settings-tooltip"
        @update:model-value="setTrackSales"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import type { FlowSettingsBulk } from '@/types/flow-settings';
import { ButtonGroup } from '@/components/flows/operators/shared';

export default defineComponent({
  name: 'FlowSettingsBulk',

  components: {
    InputText,
    ButtonGroup,
  },

  props: {
    settings: {
      type: Object as PropType<FlowSettingsBulk>,
      required: true,
    },
    validationErrors: {
      type: Object as PropType<Record<string, string[]>>,
      default: () => ({}),
    },
  },

  emits: ['update'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Local reactive copy of settings
    const localSettings = reactive<FlowSettingsBulk>({ ...props.settings });

    // Watch for external settings changes
    watch(() => props.settings, (newSettings) => {
      Object.assign(localSettings, newSettings);
    }, { deep: true });

    // Emit helpers
    const emitUpdate = (field: keyof FlowSettingsBulk, value: any) => {
      (localSettings as any)[field] = value;
      emit('update', { [field]: value });
    };

    const yesNoOptions = computed(() => [
      { value: 1, label: t('yes') },
      { value: 0, label: t('no') },
    ]);

    const setTrackSales = (value: number) => {
      emitUpdate('track_sales', value as 0 | 1);
    };

    return {
      t,
      localSettings,
      emitUpdate,
      yesNoOptions,
      setTrackSales,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-settings-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__tooltip-icon {
    color: #9ca3af;
    cursor: help;
    font-size: 0.75rem;
  }

  &__input {
    width: 100%;
  }


}

.field-error-message {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.375rem;

  i {
    color: #ef4444;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  small {
    font-size: 0.8125rem;
    line-height: 1.4;
  }
}
</style>
